import { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout, Space, Row, Col, Grid, Drawer, Button } from 'antd';
import {
  MenuOutlined,
  CloseOutlined,
  ExportOutlined,
  LoginOutlined,
  UserAddOutlined,
} from '@ant-design/icons';
import { SERVICE_TITLE } from '@shared/config/service-config';
import '../../styles/public-buttons.css';
import '../../styles/public-header.css';

const { Header } = Layout;
const { useBreakpoint } = Grid;

type NavItem =
  | { label: string; to: string }
  | { label: string; href: string; external?: boolean };

const NAV_ITEMS: NavItem[] = [
  { label: '機能紹介', to: '/service/features' },
  { label: '料金プラン', to: '/service/plans' },
  { label: '業界別デモ', to: '/industry-guides' },
];

interface PublicHeaderProps {
  /** スティッキーヘッダーにするか */
  sticky?: boolean;
  /** 背景を透明にするか（ヒーローセクション用） */
  transparent?: boolean;
  /** ボックスシャドウを表示するか */
  showShadow?: boolean;
}

export const PublicHeader: FC<PublicHeaderProps> = ({
  sticky = true,
  transparent = false,
  showShadow = true,
}) => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleNavClick = () => {
    setDrawerOpen(false);
  };

  const renderNavLink = (item: NavItem) => {
    if ('href' in item) {
      return (
        <a
          key={item.href}
          href={item.href}
          target={item.external ? '_blank' : undefined}
          rel={item.external ? 'noopener noreferrer' : undefined}
          className="public-header-nav-link"
          onClick={handleNavClick}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span>{item.label}</span>
            {item.external && <ExportOutlined />}
          </span>
        </a>
      );
    }
    return (
      <Link
        key={item.to}
        to={item.to}
        className="public-header-nav-link"
        onClick={handleNavClick}
      >
        {item.label}
      </Link>
    );
  };

  const renderDesktopNav = () => (
    <Space size="middle" className="public-header-nav">
      {NAV_ITEMS.map(renderNavLink)}
      <Link
        to="/register"
        className="public-btn public-btn--brand"
      >
        新規登録
      </Link>
      <Link
        to="/login"
        className="public-btn public-btn--primary"
      >
        ログイン
      </Link>
    </Space>
  );

  const renderMobileDrawer = () => (
    <>
      <Button
        type="text"
        icon={<MenuOutlined style={{ fontSize: 20 }} />}
        onClick={() => setDrawerOpen(true)}
        className="public-header-menu-btn"
        aria-label="メニューを開く"
      />
      <Drawer
        title={
          <Link
            to="/"
            className="public-header-logo"
            onClick={handleNavClick}
          >
            {SERVICE_TITLE}
          </Link>
        }
        placement="right"
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
        width={280}
        closeIcon={<CloseOutlined />}
        className="public-header-drawer"
      >
        <div className="public-header-drawer-content">
          <nav className="public-header-drawer-nav">
            {NAV_ITEMS.map(renderNavLink)}
          </nav>
          <div className="public-header-drawer-actions">
            <Link
              to="/register"
              className="public-btn public-btn--brand public-btn--block"
              onClick={handleNavClick}
            >
              <UserAddOutlined />
              新規登録
            </Link>
            <Link
              to="/login"
              className="public-btn public-btn--primary public-btn--block"
              onClick={handleNavClick}
            >
              <LoginOutlined />
              ログイン
            </Link>
          </div>
        </div>
      </Drawer>
    </>
  );

  return (
    <Header
      className={`public-header ${transparent ? 'public-header--transparent' : ''}`}
      style={{
        position: sticky ? 'sticky' : 'relative',
        boxShadow: showShadow && !transparent ? '0 2px 8px rgba(0,0,0,0.08)' : 'none',
      }}
    >
      <Row align="middle" justify="space-between" className="public-header-row">
        <Col>
          <Link to="/" className="public-header-logo">
            {SERVICE_TITLE}
          </Link>
        </Col>
        <Col>
          {isMobile ? renderMobileDrawer() : renderDesktopNav()}
        </Col>
      </Row>
    </Header>
  );
};

export default PublicHeader;
